import { create } from 'zustand';
import {
  authApi,
  UserResponse,
  clearAccessToken,
  hasAccessToken,
  isTokenInvalidError,
  onAuthBroadcast,
} from '@/api/client';
import { useCharacterStore } from './characterStore';

type AuthSyncEvent = 'login' | 'logout';

const AUTH_BOOTSTRAP_SKIP_PREFIXES = [
  '/legal',
  '/login',
  '/register',
  '/forgot-password',
  '/force-password-change',
  '/force-2fa-setup',
];

const shouldSkipInitialAuthCheck = (): boolean =>
  AUTH_BOOTSTRAP_SKIP_PREFIXES.some((prefix) =>
    window.location.pathname === prefix || window.location.pathname.startsWith(`${prefix}/`)
  );

interface AuthState {
  user: UserResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  setUser: (user: UserResponse | null) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  syncAuth: (event?: AuthSyncEvent) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,

  setUser: (user) => {
    set({
      user,
      isAuthenticated: !!user,
      isLoading: false
    });
  },

  login: async (email: string, password: string) => {
    try {
      await authApi.login({ email, password });
      const user = await authApi.getCurrentUser();
      set({
        user: user.data,
        isAuthenticated: true,
        isLoading: false
      });
    } catch (error) {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false
      });
      throw error;
    }
  },

  logout: () => {
    authApi.logout();
    useCharacterStore.getState().clearCharacters();
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false
    });
  },

  checkAuth: async () => {
    try {
      // Access token lives in memory. On page load or another tab's login event,
      // restore it silently from the HttpOnly refresh cookie.
      if (!hasAccessToken()) {
        await authApi.refresh();
      }

      const user = await authApi.getCurrentUser();
      set({
        user: user.data,
        isAuthenticated: true,
        isLoading: false
      });
    } catch (error) {
      // Чистим access ТОЛЬКО при реальной невалидности (401). Транзиентный сбой
      // (network/timeout/5xx, моргнувший бэкенд) НЕ должен разлогинивать текущую
      // вкладку, если в памяти уже был валидный access.
      if (isTokenInvalidError(error)) {
        clearAccessToken();
        set({ user: null, isAuthenticated: false, isLoading: false });
      } else {
        // Access не трогаем; снимаем загрузку, состояние авторизации не меняем.
        set({ isLoading: false });
      }
    }
  },

  // Синхронизация состояния между вкладками через BroadcastChannel.
  syncAuth: (event) => {
    if (event === 'logout') {
      clearAccessToken();
      useCharacterStore.getState().clearCharacters();
      set({ user: null, isAuthenticated: false, isLoading: false });
      return;
    }

    if (event === 'login' || (!event && !get().isAuthenticated)) {
      get().checkAuth();
    }
  }
}));

// Авто-проверка при загрузке страницы (guard prevents duplicate registration on HMR)
if (typeof window !== 'undefined' && !(window as any).__pf2e_auth_init__) {
  (window as any).__pf2e_auth_init__ = true;

  setTimeout(() => {
    if (shouldSkipInitialAuthCheck()) {
      useAuthStore.setState({ isLoading: false });
      return;
    }
    useAuthStore.getState().checkAuth();
  }, 0);

  onAuthBroadcast((event) => {
    useAuthStore.getState().syncAuth(event);
  });
}
